import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { vars } from '../../theme';
import {
    Close24,
    Download24,
    FilePlain24,
    Image24,
    Download16,
    Trash24,
    IconProps
} from '@honeybook/icons';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Z_INDEX } from '../../styles/variables';
import { useLockBodyScroll } from 'react-use';
import HBText from '../HBText/HBText';
import HBIconButton from '../HBIconButton/HBIconButton';
import { applyOpacity } from '../../utils/color.utils';
import { getCloudinaryPathFromCloudinaryUrl } from '../HBMediaElement/HBMediaElement';
import {
    generatePdfPreviewUrl,
    isCloudinaryImage,
    isPdfUrl
} from '../common/image.utils';
import { createPortal } from 'react-dom';
import HBButton from '../HBButton/HBButton';
import { CloudinaryContext, Image as CloudinaryImage } from 'cloudinary-react';
import { useWindowSize } from '../../hooks/useWindowSize';

interface BaseProps {
    /** Display name shown in the preview header. */
    fileName: string;
    /** URL of the file to preview; determines whether PDF, image, or unsupported preview is rendered. */
    fileUrl: string;
    /** Cloudinary public ID used for optimized image delivery; falls back to extracting from `fileUrl` if omitted. */
    cloudinaryPublicId?: string;
    /** Called after the close animation finishes, whether triggered by the close button, the Escape key, or a backdrop click. Use it to unmount the component. */
    onClose: () => void;
    /** Callback for the download action in the header; when omitted, the download button is still rendered but non-functional. */
    onDownload?: () => void;
    /** Callback for the delete action; only shown on mobile/tablet viewports. */
    onDelete?: () => void;
}

interface GenericPreviewProps extends BaseProps {
    Content: ReactElement;
    icon: (props: IconProps) => JSX.Element;
}

export interface Props extends BaseProps {
    /** Controls preview rendering: 'image' uses Cloudinary optimized display, 'file' checks for PDF support or shows an unsupported fallback. */
    fileType: 'file' | 'image';
}

function getPDFImageSize(windowSizeData: ReturnType<typeof useWindowSize>): {
    width: number;
} {
    const { isLargeUp, isMediumUp, windowSize } = windowSizeData;

    if (isLargeUp) {
        return {
            width: 670
        };
    }

    // in mobile/tablet we want coast to coast with padding on each side
    const padding = isMediumUp ? 80 : 20;
    const width = windowSize!.width - padding * 2;

    return { width };
}

const PDFPreview = (props: Props) => {
    const { width } = getPDFImageSize(useWindowSize());
    const path = generatePdfPreviewUrl({ url: props.fileUrl, width });

    const Content = <img src={path} alt={''} />;

    return <GenericPreview {...props} Content={Content} icon={FilePlain24} />;
};

const ImagePreview = (props: Props) => {
    const Content = isCloudinaryImage(props.fileUrl) ? (
        <CloudinaryContext secure cloudName="honeybook">
            <CloudinaryImage
                publicId={
                    props.cloudinaryPublicId ||
                    getCloudinaryPathFromCloudinaryUrl(props.fileUrl)
                }
                fetchFormat="auto"
                quality="auto"
                flags="lossy"
                crop="limit"
            />
        </CloudinaryContext>
    ) : (
        <img src={props.fileUrl} alt={''} />
    );

    return <GenericPreview {...props} Content={Content} icon={Image24} />;
};

const UnsupportedPreview = (props: Props) => {
    const { onDownload } = props;
    return (
        <GenericPreview
            {...props}
            Content={
                <UnsupportedMessage>
                    <HBText type="title18" color="text.primary">
                        No preview available
                    </HBText>
                    {onDownload && (
                        <HBButton
                            onClick={onDownload}
                            icon={<Download16 />}
                            buttonTheme="primary"
                            size="sm"
                            testIdName="download"
                        >
                            Download
                        </HBButton>
                    )}
                </UnsupportedMessage>
            }
            icon={FilePlain24}
        />
    );
};

const GenericPreview = ({
    onClose,
    onDownload,
    onDelete,
    fileName,
    Content,
    icon: Icon
}: GenericPreviewProps) => {
    const [isShown, setIsShown] = useState(true);
    const contentContainerRef = useRef<HTMLDivElement>(null);
    const { isMediumUp } = useWindowSize();

    useLockBodyScroll(isShown);

    const onAnimationComplete = () => {
        if (!isShown) {
            onClose();
        }
    };

    useEffect(() => {
        const contentContainer = contentContainerRef.current;

        const onKeyUp = (ev: KeyboardEvent) => {
            if (ev.key === 'Escape') {
                ev.stopPropagation();
                setIsShown(false);
            }
        };

        const onBackdropClick = (ev: MouseEvent) => {
            if (ev.target === contentContainer) {
                setIsShown(false);
            }
        };

        if (isShown) {
            document.addEventListener('keyup', onKeyUp, true);
            contentContainer.addEventListener('click', onBackdropClick, true);
            return () => {
                document.removeEventListener('keyup', onKeyUp, true);
                contentContainer.removeEventListener(
                    'click',
                    onBackdropClick,
                    true
                );
            };
        }
    }, [isShown]);

    return createPortal(
        <Backdrop
            initial={{ opacity: 0 }}
            animate={isShown ? { opacity: 1 } : { opacity: 0 }}
            transition={transition}
            onAnimationComplete={onAnimationComplete}
        >
            <Container>
                <Header>
                    <FileDescription>
                        <Icon color={vars.colors.text.invertedPrimary} />
                        <HBText
                            type="emphasis18"
                            color="text.invertedPrimary"
                            ellipsis
                        >
                            {fileName}
                        </HBText>
                    </FileDescription>
                    <Actions>
                        <HBIconButton
                            buttonTheme="light-invisible"
                            icon={Download24}
                            label="download"
                            onClick={onDownload}
                            size="sm"
                            testIdName="download"
                        />
                        {!isMediumUp && onDelete && (
                            <HBIconButton
                                buttonTheme="light-invisible"
                                icon={Trash24}
                                label="delete"
                                onClick={onDelete}
                                size="sm"
                                testIdName="delete"
                            />
                        )}
                        <HBIconButton
                            buttonTheme="light-invisible"
                            icon={Close24}
                            label="close"
                            onClick={() => setIsShown(false)}
                            size="sm"
                            testIdName="close"
                        />
                    </Actions>
                </Header>
                <ContentContainer ref={contentContainerRef}>
                    {Content}
                </ContentContainer>
            </Container>
        </Backdrop>,
        document.body
    );
};

const HBFilePreview = (props: Props) => {
    if (props.fileType === 'image') {
        return <ImagePreview {...props} />;
    }

    const isPdf = isPdfUrl(props.fileUrl);
    if (isPdf) {
        return <PDFPreview {...props} />;
    }

    return <UnsupportedPreview {...props} />;
};

export default HBFilePreview;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    padding: 16px;
    box-sizing: border-box;
    height: 100%;
`;

const ContentContainer = styled.div`
    justify-content: center;
    align-items: center;
    display: flex;
    padding-top: 40px;
    &,
    * {
        height: 100%;
    }
    img {
        object-fit: contain;
        max-width: 100%;
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    position: absolute;
    gap: 16px;
    padding: 16px;
    inset: 0 0 auto;
    background: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0.65) 0,
        transparent 100%
    );
`;

const FileDescription = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const Actions = styled.div`
    display: flex;
`;

const transition = { duration: 0.3, type: 'tween', ease: 'easeInOut' };

const Backdrop = styled(motion.div)`
    position: fixed;
    z-index: ${Z_INDEX.POPOVER};
    inset: 0;
    background-color: ${applyOpacity(
        vars.colors.surface.containerInverted,
        70
    )};
`;

const UnsupportedMessage = styled.div`
    display: flex;
    width: 382px;
    padding: 24px;
    flex-direction: row;
    align-items: center;
    gap: 24px;
    border-radius: ${vars.radius.xs};
    background: ${vars.colors.surface.item};

    &,
    * {
        height: auto;
    }

    button {
        height: 36px;
    }
`;
